import {PlantHierarchyType, usePlantHierarchyQuery} from '@hproduce/suite-core'
import {useMemo} from 'react'

import {FeatureMenuEntry, MenuNodeType} from '../api/types/featuresMenu'
import {useFeatureName} from '../app/features/useFeatureName'
import {useFeaturesMenu} from '../app/welcomeFlow/useFeaturesMenu'
import {collectHierarchyNodes, LocationOption} from '../utils/collectHierarchyNodes'

export const useLocationOptions = () => {
  const {data: hierarchyData, isPending} = usePlantHierarchyQuery()
  const {featuresMenu} = useFeaturesMenu()
  const featureName = useFeatureName()

  const allowedTypes = useMemo((): PlantHierarchyType[] => {
    if (!featuresMenu?.length || !featureName) return []

    function findFeatureIterative(nodes: FeatureMenuEntry[]): FeatureMenuEntry | undefined {
      const stack = [...nodes]
      while (stack.length) {
        const node = stack.pop()
        if (node?.type === MenuNodeType.Feature && node.name === featureName) {
          return node
        }
        if (node?.type === MenuNodeType.Folder && node.children.length > 0) {
          stack.push(...node.children)
        }
      }
      return undefined
    }

    const feature = findFeatureIterative(featuresMenu)

    if (!feature || feature.type !== MenuNodeType.Feature) return []

    return feature.scope
  }, [featuresMenu, featureName])

  const options = useMemo(() => {
    const hierarchy = hierarchyData
    if (!hierarchy) return [] as LocationOption[]

    const typesToCollect =
      allowedTypes.length === 0 ? Object.values(PlantHierarchyType) : allowedTypes

    return collectHierarchyNodes(hierarchy, typesToCollect)
  }, [hierarchyData, allowedTypes])

  return {
    options,
    isLoading: isPending,
    allowedTypes
  }
}
